import {
  ArrowsRightLeftIcon,
  CloudArrowUpIcon,
  CpuChipIcon,
  CircleStackIcon,
  ChartBarSquareIcon,
  SparklesIcon,
  DocumentMagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { PLATFORM_ENGINEERING_SECTIONS } from '../../../../content/enterpriseMessaging';
import {
  CapabilityCardFeatured,
  CapabilityGroup,
  renderCapabilityItems,
} from '../../shared/CapabilityCards';

const FEATURE_GROUPS = [
  {
    label: 'Warehouse & AIM migration',
    accent: 'bg-accent-50/40 text-accent-700',
    borderAccent: 'border-l-accent-400',
    iconBg: 'bg-accent-50 text-accent-700',
    items: [
      {
        icon: ArrowsRightLeftIcon,
        title: 'Teradata, SQL Server & warehouse migration',
        description:
          'AIM-assisted paths from Teradata, SQL Server, and on-prem warehouses into governed Snowflake, with dbt models, lineage, and cutover playbooks your team can operate.',
      },
      {
        icon: SparklesIcon,
        title: 'AIM & migration agents',
        description:
          'AI-assisted migration (AIM) and Snowflake migration agents accelerate discovery, mapping, and delivery, from pipeline delivery to governed domain memory.',
      },
    ],
  },
  {
    label: 'BI & cloud platform',
    accent: 'bg-[rgba(0,18,63,0.08)] text-brand-900',
    borderAccent: 'border-l-brand-900',
    iconBg: 'bg-[rgba(0,18,63,0.08)] text-brand-900',
    items: [
      {
        icon: ChartBarSquareIcon,
        title: 'Legacy BI, Tableau & QuickSight modernization',
        description:
          'Move analytics from the BI layer to the Snowflake data layer, Power BI semantic uplift, Tableau migration, Amazon QuickSight design, and searchable dashboard marketplaces.',
      },
      {
        icon: CloudArrowUpIcon,
        title: 'AWS, Azure & platform integrations',
        description:
          'Data platform modernization on AWS and Azure, ingestion, Fabric and QuickSight connectivity, Terraform, and pipelines that bridge source systems to governed Snowflake.',
      },
    ],
  },
  {
    label: 'Semantic intelligence & enterprise AI',
    accent: 'bg-brand-50/40 text-brand-700',
    borderAccent: 'border-l-brand-500',
    iconBg: 'bg-brand-50 text-brand-600',
    columns: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    items: [
      {
        icon: CircleStackIcon,
        title: 'Enterprise semantic layer design',
        description:
          'Certified metrics, domain memory for analytics agents, and faster iteration on semantic layers, close the context gap between data, metrics, and decisions.',
      },
      {
        icon: CpuChipIcon,
        title: 'Snowflake-centered enterprise AI',
        description:
          'Snowflake Cortex, conversational analytics on governed data, Streamlit application engineering, and decision intelligence agents on trusted foundations.',
      },
      {
        icon: DocumentMagnifyingGlassIcon,
        title: 'Cortex Search & document intelligence',
        description:
          'Make PDFs, policies, contracts, handbooks, tickets, and documentation searchable and usable by AI agents, governed enterprise memory from high-volume document repositories.',
        featured: true,
      },
    ],
  },
];

const FEATURED_PLATFORM = {
  icon: CircleStackIcon,
  title: 'Unified application architecture',
  description:
    'We design application architectures combining Snowflake for analytical, semantic, and AI workloads with Postgres where transactional patterns require it. On top, we build Java services, React interfaces, and Streamlit apps.',
  iconBg: 'bg-brand-50 text-brand-600',
  borderAccent: 'border-l-brand-500',
};

export default function Features() {
  const section = PLATFORM_ENGINEERING_SECTIONS.snowflake;

  return (
    <section className="section">
      <div className="container-x">
        <div className="max-w-3xl">
          <p className="eyebrow">{section.eyebrow}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-ink-900">
            {section.title}
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-ink-600">{section.body}</p>
        </div>

        <div className="mt-12 space-y-12 lg:space-y-14">
          {FEATURE_GROUPS.map((group) => (
            <CapabilityGroup
              key={group.label}
              label={group.label}
              accent={group.accent}
              borderAccent={group.borderAccent}
              iconBg={group.iconBg}
              columns={group.columns}
            >
              {renderCapabilityItems(group.items, {
                iconBg: group.iconBg,
                borderAccent: group.borderAccent,
              })}
            </CapabilityGroup>
          ))}

          <div>
            <p className="mb-5 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide bg-accent-50/40 text-accent-700">
              Platform unification
            </p>
            <CapabilityCardFeatured {...FEATURED_PLATFORM} />
          </div>
        </div>
      </div>
    </section>
  );
}
